<script>
import { reactive, onMounted, ref, computed } from "vue";
import aiConfigRepository from "@/services/api/aiConfigRepository";

export default {
  name: "ConfiguracaoIA",
  setup() {
    const loading = ref(false);
    const saving = ref(false);
    const testing = ref(false);
    const activeTab = ref("config");

    const state = reactive({
      coachConfig: null,
      studentConfig: null,
      coachPrompt: null,
      studentPrompt: null,
      availableModels: {},
      hasCustomApiKey: false,
    });

    const formConfig = reactive({
      user_type: "coach",
      model: "gpt-3.5-turbo",
      max_tokens: 500,
      temperature: 0.7,
      daily_limit: 50,
      is_active: true,
    });

    const formApiKey = reactive({
      api_key: "",
      showKey: false,
    });

    const formPrompt = reactive({
      id: null,
      user_type: "coach",
      template_name: "",
      system_prompt: "",
      is_active: true,
    });

    const message = reactive({
      type: "",
      text: "",
    });

    const currentConfig = computed(() => {
      return formConfig.user_type === "coach"
        ? state.coachConfig
        : state.studentConfig;
    });

    const currentPrompt = computed(() => {
      return formPrompt.user_type === "coach"
        ? state.coachPrompt
        : state.studentPrompt;
    });

    onMounted(() => {
      loadConfig();
    });

    async function loadConfig() {
      loading.value = true;
      try {
        const response = await aiConfigRepository.getGlobalConfig();
        if (response.data.success) {
          state.coachConfig = response.data.data.coach_config;
          state.studentConfig = response.data.data.student_config;
          state.coachPrompt = response.data.data.coach_prompt;
          state.studentPrompt = response.data.data.student_prompt;
          state.availableModels = response.data.data.available_models;
          state.hasCustomApiKey = response.data.data.has_custom_api_key;

          // Preenche o form com a config do coach
          if (state.coachConfig) {
            Object.assign(formConfig, {
              user_type: "coach",
              model: state.coachConfig.model,
              max_tokens: state.coachConfig.max_tokens,
              temperature: parseFloat(state.coachConfig.temperature),
              daily_limit: state.coachConfig.daily_limit,
              is_active: state.coachConfig.is_active,
            });
          }

          // Preenche o form de prompt
          if (state.coachPrompt) {
            Object.assign(formPrompt, {
              id: state.coachPrompt.id,
              user_type: "coach",
              template_name: state.coachPrompt.template_name,
              system_prompt: state.coachPrompt.system_prompt,
              is_active: state.coachPrompt.is_active,
            });
          }
        }
      } catch (error) {
        console.error("Erro ao carregar configurações:", error);
        showMessage("error", "Erro ao carregar configurações");
      } finally {
        loading.value = false;
      }
    }

    function onUserTypeChange() {
      const config = currentConfig.value;
      const prompt = currentPrompt.value;

      if (config) {
        formConfig.model = config.model;
        formConfig.max_tokens = config.max_tokens;
        formConfig.temperature = parseFloat(config.temperature);
        formConfig.daily_limit = config.daily_limit;
        formConfig.is_active = config.is_active;
      }

      if (prompt) {
        formPrompt.id = prompt.id;
        formPrompt.template_name = prompt.template_name;
        formPrompt.system_prompt = prompt.system_prompt;
        formPrompt.is_active = prompt.is_active;
      } else {
        formPrompt.id = null;
        formPrompt.template_name = "";
        formPrompt.system_prompt = "";
        formPrompt.is_active = true;
      }
    }

    async function saveConfig() {
      saving.value = true;
      try {
        const response = await aiConfigRepository.updateGlobalConfig(formConfig);
        if (response.data.success) {
          showMessage("success", "Configuração salva com sucesso!");
          loadConfig();
        } else {
          showMessage("error", response.data.message || "Erro ao salvar");
        }
      } catch (error) {
        console.error("Erro ao salvar:", error);
        showMessage("error", "Erro ao salvar configuração");
      } finally {
        saving.value = false;
      }
    }

    async function saveApiKey() {
      if (!formApiKey.api_key) {
        showMessage("error", "Informe a API Key");
        return;
      }

      saving.value = true;
      try {
        const response = await aiConfigRepository.updateApiKey(formApiKey.api_key);
        if (response.data.success) {
          showMessage("success", "API Key salva com sucesso!");
          formApiKey.api_key = "";
          state.hasCustomApiKey = true;
        } else {
          showMessage("error", response.data.message || "Erro ao salvar");
        }
      } catch (error) {
        console.error("Erro ao salvar API Key:", error);
        showMessage("error", "Erro ao salvar API Key");
      } finally {
        saving.value = false;
      }
    }

    async function removeApiKey() {
      if (!confirm("Remover a API Key customizada? O sistema usará a configuração padrão do servidor.")) {
        return;
      }

      saving.value = true;
      try {
        const response = await aiConfigRepository.removeApiKey();
        if (response.data.success) {
          showMessage("success", "API Key removida com sucesso!");
          state.hasCustomApiKey = false;
        }
      } catch (error) {
        console.error("Erro ao remover API Key:", error);
        showMessage("error", "Erro ao remover API Key");
      } finally {
        saving.value = false;
      }
    }

    async function savePrompt() {
      if (!formPrompt.template_name || !formPrompt.system_prompt) {
        showMessage("error", "Preencha todos os campos do prompt");
        return;
      }

      saving.value = true;
      try {
        let response;
        if (formPrompt.id) {
          response = await aiConfigRepository.updatePrompt(formPrompt.id, {
            template_name: formPrompt.template_name,
            system_prompt: formPrompt.system_prompt,
            is_active: formPrompt.is_active,
          });
        } else {
          response = await aiConfigRepository.createPrompt({
            user_type: formPrompt.user_type,
            template_name: formPrompt.template_name,
            system_prompt: formPrompt.system_prompt,
            is_active: formPrompt.is_active,
          });
        }

        if (response.data.success) {
          showMessage("success", "Prompt salvo com sucesso!");
          loadConfig();
        } else {
          showMessage("error", response.data.message || "Erro ao salvar");
        }
      } catch (error) {
        console.error("Erro ao salvar prompt:", error);
        showMessage("error", "Erro ao salvar prompt");
      } finally {
        saving.value = false;
      }
    }

    async function testConnection() {
      testing.value = true;
      try {
        const response = await aiConfigRepository.testConnection();
        if (response.data.success) {
          showMessage("success", "Conexão estabelecida com sucesso!");
        } else {
          showMessage("error", response.data.message || "Falha na conexão");
        }
      } catch (error) {
        console.error("Erro ao testar conexão:", error);
        showMessage("error", error.response?.data?.message || "Erro ao testar conexão");
      } finally {
        testing.value = false;
      }
    }

    function showMessage(type, text) {
      message.type = type;
      message.text = text;
      setTimeout(() => {
        message.type = "";
        message.text = "";
      }, 5000);
    }

    return {
      loading,
      saving,
      testing,
      activeTab,
      state,
      formConfig,
      formApiKey,
      formPrompt,
      message,
      currentConfig,
      currentPrompt,
      onUserTypeChange,
      saveConfig,
      saveApiKey,
      removeApiKey,
      savePrompt,
      testConnection,
    };
  },
};
</script>

<template>
  <div class="container-fluid">
    <div class="row">
      <div class="col-12">
        <h4 class="mb-4">
          <i class="material-icons align-middle">smart_toy</i>
          Configurações de IA
        </h4>
      </div>
    </div>

    <!-- Mensagem de feedback -->
    <div v-if="message.text" class="row mb-3">
      <div class="col-12">
        <div
          class="alert"
          :class="message.type === 'success' ? 'alert-success' : 'alert-danger'"
          role="alert"
        >
          {{ message.text }}
        </div>
      </div>
    </div>

    <!-- Loading -->
    <div v-if="loading" class="text-center py-5">
      <div class="spinner-border text-primary" role="status">
        <span class="visually-hidden">Carregando...</span>
      </div>
    </div>

    <!-- Conteúdo -->
    <div v-else>
      <!-- Tabs -->
      <ul class="nav nav-tabs mb-4">
        <li class="nav-item">
          <a
            class="nav-link"
            :class="{ active: activeTab === 'config' }"
            href="#"
            @click.prevent="activeTab = 'config'"
          >
            <i class="material-icons align-middle me-1">settings</i>
            Configurações Gerais
          </a>
        </li>
        <li class="nav-item">
          <a
            class="nav-link"
            :class="{ active: activeTab === 'apikey' }"
            href="#"
            @click.prevent="activeTab = 'apikey'"
          >
            <i class="material-icons align-middle me-1">vpn_key</i>
            API Key
          </a>
        </li>
        <li class="nav-item">
          <a
            class="nav-link"
            :class="{ active: activeTab === 'prompts' }"
            href="#"
            @click.prevent="activeTab = 'prompts'"
          >
            <i class="material-icons align-middle me-1">description</i>
            Prompts
          </a>
        </li>
      </ul>

      <!-- Tab: Configurações Gerais -->
      <div v-show="activeTab === 'config'" class="card border">
        <div class="card-body">
          <h5 class="card-title mb-4">Configurações do Modelo</h5>

          <div class="row mb-4">
            <div class="col-md-6">
              <label class="form-label fw-bold">Tipo de Usuário</label>
              <select
                v-model="formConfig.user_type"
                class="form-select"
                @change="onUserTypeChange"
              >
                <option value="coach">Treinador (Geração de Treinos)</option>
                <option value="student">Aluno (Chat)</option>
              </select>
            </div>
            <div class="col-md-6">
              <label class="form-label fw-bold">Modelo OpenAI</label>
              <select v-model="formConfig.model" class="form-select">
                <option
                  v-for="(label, value) in state.availableModels"
                  :key="value"
                  :value="value"
                >
                  {{ label }}
                </option>
              </select>
            </div>
          </div>

          <div class="row mb-4">
            <div class="col-md-4">
              <label class="form-label fw-bold">Max Tokens</label>
              <input
                v-model.number="formConfig.max_tokens"
                type="number"
                class="form-control"
                min="100"
                max="4000"
              />
              <small class="text-muted">Máximo de tokens por resposta (100-4000)</small>
            </div>
            <div class="col-md-4">
              <label class="form-label fw-bold">Temperatura</label>
              <input
                v-model.number="formConfig.temperature"
                type="number"
                class="form-control"
                min="0"
                max="1"
                step="0.1"
              />
              <small class="text-muted">Criatividade das respostas (0-1)</small>
            </div>
            <div class="col-md-4">
              <label class="form-label fw-bold">Limite Diário</label>
              <input
                v-model.number="formConfig.daily_limit"
                type="number"
                class="form-control"
                min="1"
                max="1000"
              />
              <small class="text-muted">Mensagens por dia por usuário</small>
            </div>
          </div>

          <div class="row mb-4">
            <div class="col-md-6">
              <div class="form-check form-switch">
                <input
                  v-model="formConfig.is_active"
                  class="form-check-input"
                  type="checkbox"
                  id="isActive"
                />
                <label class="form-check-label" for="isActive">
                  IA Ativa para {{ formConfig.user_type === 'coach' ? 'Treinadores' : 'Alunos' }}
                </label>
              </div>
            </div>
          </div>

          <div class="d-flex gap-2">
            <button
              class="btn btn-primary"
              :disabled="saving"
              @click="saveConfig"
            >
              <span v-if="saving" class="spinner-border spinner-border-sm me-1"></span>
              Salvar Configurações
            </button>
          </div>
        </div>
      </div>

      <!-- Tab: API Key -->
      <div v-show="activeTab === 'apikey'" class="card border">
        <div class="card-body">
          <h5 class="card-title mb-4">Chave de API da OpenAI</h5>

          <div class="alert alert-info mb-4">
            <i class="material-icons align-middle me-2">info</i>
            <strong>Status:</strong>
            {{ state.hasCustomApiKey ? 'Usando API Key customizada' : 'Usando API Key do servidor' }}
          </div>

          <div class="row mb-4">
            <div class="col-md-8">
              <label class="form-label fw-bold">Nova API Key</label>
              <div class="input-group">
                <input
                  v-model="formApiKey.api_key"
                  :type="formApiKey.showKey ? 'text' : 'password'"
                  class="form-control"
                  placeholder="sk-..."
                />
                <button
                  class="btn btn-outline-secondary"
                  type="button"
                  @click="formApiKey.showKey = !formApiKey.showKey"
                >
                  <i class="material-icons">
                    {{ formApiKey.showKey ? 'visibility_off' : 'visibility' }}
                  </i>
                </button>
              </div>
              <small class="text-muted">A chave será criptografada antes de salvar</small>
            </div>
          </div>

          <div class="d-flex gap-2">
            <button
              class="btn btn-primary"
              :disabled="saving || !formApiKey.api_key"
              @click="saveApiKey"
            >
              <span v-if="saving" class="spinner-border spinner-border-sm me-1"></span>
              Salvar API Key
            </button>
            <button
              v-if="state.hasCustomApiKey"
              class="btn btn-outline-danger"
              :disabled="saving"
              @click="removeApiKey"
            >
              Remover API Key
            </button>
            <button
              class="btn btn-outline-info"
              :disabled="testing"
              @click="testConnection"
            >
              <span v-if="testing" class="spinner-border spinner-border-sm me-1"></span>
              Testar Conexão
            </button>
          </div>
        </div>
      </div>

      <!-- Tab: Prompts -->
      <div v-show="activeTab === 'prompts'" class="card border">
        <div class="card-body">
          <h5 class="card-title mb-4">Prompts do Sistema</h5>

          <div class="row mb-4">
            <div class="col-md-6">
              <label class="form-label fw-bold">Tipo de Usuário</label>
              <select
                v-model="formPrompt.user_type"
                class="form-select"
                @change="onUserTypeChange"
              >
                <option value="coach">Treinador</option>
                <option value="student">Aluno</option>
              </select>
            </div>
            <div class="col-md-6">
              <label class="form-label fw-bold">Nome do Template</label>
              <input
                v-model="formPrompt.template_name"
                type="text"
                class="form-control"
                placeholder="Ex: Assistente de Treinos"
              />
            </div>
          </div>

          <div class="row mb-4">
            <div class="col-12">
              <label class="form-label fw-bold">Prompt do Sistema</label>
              <textarea
                v-model="formPrompt.system_prompt"
                class="form-control"
                rows="12"
                placeholder="Digite o prompt do sistema aqui...

Você pode usar placeholders como:
{student_name} - Nome do aluno
{student_weight} - Peso do aluno
{student_height} - Altura do aluno
{training_context} - Contexto do treino atual"
              ></textarea>
              <small class="text-muted">
                Use placeholders entre chaves para dados dinâmicos. Ex: {'{'}student_name{'}'}
              </small>
            </div>
          </div>

          <div class="row mb-4">
            <div class="col-md-6">
              <div class="form-check form-switch">
                <input
                  v-model="formPrompt.is_active"
                  class="form-check-input"
                  type="checkbox"
                  id="promptActive"
                />
                <label class="form-check-label" for="promptActive">
                  Prompt Ativo
                </label>
              </div>
            </div>
          </div>

          <div class="d-flex gap-2">
            <button
              class="btn btn-primary"
              :disabled="saving"
              @click="savePrompt"
            >
              <span v-if="saving" class="spinner-border spinner-border-sm me-1"></span>
              {{ formPrompt.id ? 'Atualizar Prompt' : 'Criar Prompt' }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.nav-tabs .nav-link {
  color: #666;
}

.nav-tabs .nav-link.active {
  font-weight: bold;
}

.material-icons {
  font-size: 18px;
  vertical-align: middle;
}
</style>
